/**
 * 74.19's yards: the carve record applied (core/terrainEdits.ts applyTerrainEdit, `kind: 'carve'`) — kept apart from
 * core/carveYards.ts, which reads the setup's state, so the terrain edits import nothing of it. The design: carveYards.ts.
 */
import type { PlanetState, Shaping, TerrainEdit, Vec3 } from '../types';
import { verticesNear } from './vertexCells';
import { planetRouteGraph } from './planetRoutes';
import { groundKey } from './spinAxis';
import { dacos } from './dmath';
import { padSnapshots } from './terraceGround';

/**
 * The carve laid on `planet` (its `carved`, kept aside by the generator) with the record's yards held: the heights, the
 * generated heights and the records. Laid already: the planet unchanged; never kept aside: it throws (below).
 */
export function withCarve(planet: PlanetState, e: TerrainEdit): PlanetState {
  const c = planet.carved, spec = e.carve;
  if (!spec) return planet;
  if (!c) {
    // laid already (the setup keeps the planned heights once it is): the planet is the carve's own, nothing to do again. A
    // planet that never had a carve kept aside cannot take one — the ground would part from the mesh and the replay without a
    // word; nothing applies the log to such a planet (replays and re-entry run the setup again, the kernel takes the heights,
    // the sector rebuilds from its own generator, whose setups carry no erosion), so this throws rather than pass quietly
    if (planet.plannedHeightmap) return planet;
    throw new Error(`74.19: a 'carve' terrain record applied to planet ${planet.id}, which has no carve kept aside (core/carveYards.ts)`);
  }
  const hm = planet.heightmap, init = planet.initialHeightmap ?? planet.heightmap, cut = c.heightmap;
  const N = hm.length;
  // how far past the nearest yard each corner stands (world units; 0 inside one, Infinity past the farthest reach the rim needs)
  let most = 0;
  for (let i = 0; i < N; i++) { const x = Math.abs(cut[i] - init[i]); if (x > most) most = x; }
  const past = yardPast(planet, spec.yards, most / spec.grade);
  // a kept record's level vertices take the whole carve (its level is the carved mesh's radius there)
  const g = planetRouteGraph(planet), whole = new Uint8Array(g.count);
  for (const s of c.shapings) for (const v of c.levelVerts[s.id - 1] ?? []) whole[v] = 1;
  const heightmap = new Float32Array(N), initialHeightmap = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const dv = cut[i] - init[i];
    const lim = whole[g.nodeOf[i]] ? Infinity : spec.grade * past[i];
    if (dv === 0 || lim <= 0) { heightmap[i] = hm[i]; initialHeightmap[i] = init[i]; continue; }
    if (Math.abs(dv) <= lim) {
      initialHeightmap[i] = cut[i];
      heightmap[i] = hm[i] === init[i] ? cut[i] : hm[i] + dv;   // (as generated: the carved height to the bit)
    } else {
      const d = dv > 0 ? lim : -lim;
      initialHeightmap[i] = init[i] + d;
      heightmap[i] = hm[i] + d;
    }
  }
  const { carved: _c, ...rest } = planet;
  const laid: PlanetState = {
    ...rest, heightmap, initialHeightmap, plannedHeightmap: init,
    ...(c.shapings.length ? { shapings: c.shapings } : { shapings: undefined }),
    ...(c.screeFacets ? { screeFacets: c.screeFacets } : {}),
    ...(planet.baseGroundKey === undefined && planet.carvedGroundKey === undefined ? { carvedGroundKey: groundKey(planet) } : {}),
  };
  // (71.5 step 2: the pads' kept mesh taken again, as laying a terrace takes it — the carve is a laying, not a dent. A pad's
  // kept corners reach a facet past its yard's hold, and the carve moves those; left as the planned heights, every pad on a
  // carved world read as dented from the first tick: the dented ground path at each read on it, a rise of the cut's depth)
  return laid.shapingOrder && laid.padLaid ? { ...laid, padLaid: padSnapshots(laid) } : laid;
}

/**
 * How far past the nearest yard each heightmap corner stands (world units): 0 inside one, Infinity past `reach` beyond them
 * all. A point's ground is its facet's three corners, so a yard holds every corner within its radius and the mesh's longest
 * edge (the Story's large world's facets are ~43 people across: a 5-person yard alone held none).
 */
export function yardPast(planet: Pick<PlanetState, 'radius' | 'basePositions' | 'heightmap'>, yards: ReadonlyArray<{ dir: Vec3; radius: number }>, reach: number): Float64Array {
  const R = planet.radius, bp = planet.basePositions, N = planet.heightmap.length;
  const dirOf = (i: number) => { const x = bp[3 * i], y = bp[3 * i + 1], z = bp[3 * i + 2], l = Math.sqrt(x * x + y * y + z * z) || 1; return { x: x / l, y: y / l, z: z / l }; };
  const span = meshSpan(planet);
  const past = new Float64Array(N).fill(Infinity);
  for (const y of yards) {
    const near = verticesNear(bp, y.dir, (y.radius + span + reach) / R);
    const visit = (i: number) => {
      const d = dirOf(i), a = dacos(Math.max(-1, Math.min(1, d.x * y.dir.x + d.y * y.dir.y + d.z * y.dir.z))) * R - y.radius - span;
      const p = a > 0 ? a : 0;
      if (p < past[i]) past[i] = p;
    };
    if (near) for (const i of near) visit(i);
    else for (let i = 0; i < N; i++) visit(i);
  }
  return past;
}

/** The mesh's longest facet edge (world units): how far a point's ground reaches for its corners. */
export function meshSpan(planet: Pick<PlanetState, 'radius' | 'basePositions' | 'heightmap'>): number {
  const R = planet.radius, bp = planet.basePositions, N = planet.heightmap.length;
  const dirOf = (i: number) => { const x = bp[3 * i], y = bp[3 * i + 1], z = bp[3 * i + 2], l = Math.sqrt(x * x + y * y + z * z) || 1; return { x: x / l, y: y / l, z: z / l }; };
  let span = 0;
  for (let t = 0; t * 3 + 2 < N; t++) for (let k = 0; k < 3; k++) {
    const a = dirOf(t * 3 + k), b = dirOf(t * 3 + (k + 1) % 3);
    const e = dacos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))) * R;
    if (e > span) span = e;
  }
  return span;
}

/**
 * Lane C's guard (Nov 3): the carved records that reach a yard — any footprint point within the record's half width and
 * reach, the yard's radius and the mesh's span of its centre — dropped, as clearShapingsUnder drops one under a structure.
 * Kept, a record's level vertices take the whole carve inside a yard that holds the ground round them at the planned
 * level: a pit or a spike of the carve's depth in the yard. (On the Story's 24 seeds none reached one; the guard holds it.)
 */
export function recordsClearOfYards(planet: Pick<PlanetState, 'radius' | 'basePositions' | 'heightmap'>, shapings: readonly Shaping[], yards: ReadonlyArray<{ dir: Vec3; radius: number }>): Shaping[] {
  if (!shapings.length || !yards.length) return [...shapings];
  const R = planet.radius, span = meshSpan(planet);
  const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))) * R;
  return shapings.filter(s => {
    const f = s.footprint;
    const pts = f.kind === 'ribbon' ? f.nodes.map(n => n.dir) : [f.dir];
    const extent = (f.kind === 'ribbon' ? f.half : Math.sqrt(f.halfX * f.halfX + f.halfZ * f.halfZ)) + s.reach;
    return !yards.some(y => pts.some(p => arc(p, y.dir) <= y.radius + span + extent));
  });
}
